import { createStore } from 'vuex';
import { authApi, projectsApi, recordsApi } from '../services/api';
import { setAuthToken } from '../services/api';

const authModule = {
  namespaced: true,
  state: () => ({
    user: null,
    token: localStorage.getItem('aim_token') || null,
    loading: false,
    error: null,
  }),
  getters: {
    isAuthenticated(state) {
      return !!state.token;
    },
    userRole(state) {
      return state.user?.role || 'user';
    },
  },
  mutations: {
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_AUTH(state, { user, token }) {
      state.user = user;
      state.token = token;
    },
    CLEAR_AUTH(state) {
      state.user = null;
      state.token = null;
    },
  },
  actions: {
    async init({ state, dispatch }) {
      if (state.token) {
        setAuthToken(state.token);
        await dispatch('fetchProfile').catch(() => {
          // token invalid
          dispatch('logout');
        });
      }
    },
    async login({ commit, dispatch }, payload) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const { data } = await authApi.login(payload);
        const token = data?.data?.token;
        const user = data?.data?.user;
        if (token) {
          localStorage.setItem('aim_token', token);
          setAuthToken(token);
        }
        commit('SET_AUTH', { user, token });
        await dispatch('projects/fetchProjects', {}, { root: true });
        return data;
      } catch (err) {
        commit('SET_ERROR', err.message || err.msg || 'Login failed');
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async register({ commit }, payload) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const { data } = await authApi.register(payload);
        return data;
      } catch (err) {
        commit('SET_ERROR', err.message || 'Registration failed');
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async fetchProfile({ commit }) {
      try {
        const { data } = await authApi.getProfile();
        commit('SET_AUTH', {
          user: data?.data?.user,
          token: localStorage.getItem('aim_token'),
        });
        return data;
      } catch (err) {
        throw err;
      }
    },
    logout({ commit }) {
      localStorage.removeItem('aim_token');
      setAuthToken(null);
      commit('CLEAR_AUTH');
    },
  },
};

const projectsModule = {
  namespaced: true,
  state: () => ({
    items: [],
    pagination: null,
    current: null,
    loading: false,
    error: null,
  }),
  mutations: {
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_PROJECTS(state, { projects, pagination }) {
      state.items = projects;
      state.pagination = pagination;
    },
    SET_CURRENT(state, project) {
      state.current = project;
    },
  },
  actions: {
    async fetchProjects({ commit }, params = { page: 1, limit: 10 }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const { data } = await projectsApi.list(params);
        commit('SET_PROJECTS', {
          projects: data?.data?.projects || [],
          pagination: data?.data?.pagination || null,
        });
      } catch (err) {
        commit('SET_ERROR', err.message || 'Failed to load projects');
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async fetchProject({ commit }, id) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const { data } = await projectsApi.get(id);
        commit('SET_CURRENT', data?.data?.project || null);
      } catch (err) {
        commit('SET_ERROR', err.message || 'Failed to load project');
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },
  },
};

const recordsModule = {
  namespaced: true,
  state: () => ({
    items: [],
    pagination: null,
    loading: false,
    error: null,
  }),
  mutations: {
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_RECORDS(state, { records, pagination }) {
      state.items = records;
      state.pagination = pagination;
    },
  },
  actions: {
    async fetchRecords({ commit }, params = { page: 1, limit: 10 }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const { data } = await recordsApi.list(params);
        commit('SET_RECORDS', {
          records: data?.data?.records || [],
          pagination: data?.data?.pagination || null,
        });
      } catch (err) {
        commit('SET_ERROR', err.message || 'Failed to load records');
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },
  },
};

const store = createStore({
  modules: {
    auth: authModule,
    projects: projectsModule,
    records: recordsModule,
  },
});

// Initialize auth from localStorage
store.dispatch('auth/init');

export default store;

